import React, { useState } from 'react'
import {
  useSequencerStore,
  FX_IDS,
  type FxId,
  type StepCell,
  type TrackerRow,
} from '@/stores/sequencerStore'

/**
 * Phase 3e — always-on cell editor visualizer with lane switching.
 *
 * The older inspector used separate collapsible sections for Gain
 * (velocity), Tune (pitch), plus the FX Sends section. Clicking a
 * cell off-state greyed all of those out. The visualizer replaces
 * that with:
 *
 *   - Always interactive — click or drag even on a cell that's
 *     off; the cell flips to `on: true` automatically.
 *   - Lane-switched — Note / Pitch / Velocity / FX / Roll across
 *     the top. Each lane focuses one dimension; no more hunting
 *     through accordions.
 *   - Note lane shows the cell's microOffset (horizontal position
 *     within the step). Drag to set off-grid timing.
 *
 * Roll lane delegates to the existing RollMicroGrid component
 * (kept in TrackerInspector) so we don't re-implement the pitch
 * offsets + enable mask UI here.
 */

type Lane = 'note' | 'pitch' | 'velocity' | 'fx' | 'roll'

const LANES: ReadonlyArray<{ id: Lane; label: string }> = [
  { id: 'note', label: 'Note' },
  { id: 'pitch', label: 'Pitch' },
  { id: 'velocity', label: 'Velocity' },
  { id: 'fx', label: 'FX' },
  { id: 'roll', label: 'Roll' },
]

const PER_STEP_FX: readonly FxId[] = ['delay', 'reverb', 'bitcrush', 'stutter']

const FX_LABEL_SHORT: Record<FxId, string> = {
  filter: 'Flt',
  delay: 'Dly',
  reverb: 'Rvb',
  bitcrush: 'Bit',
  tapeStop: 'Tape',
  stutter: 'Stu',
}

interface CellVisualizerProps {
  row: TrackerRow
  stepIndex: number
  cell: StepCell
}

export const CellVisualizer: React.FC<CellVisualizerProps> = ({
  row,
  stepIndex,
  cell,
}) => {
  const [lane, setLane] = useState<Lane>('note')
  const toggleStep = useSequencerStore(s => s.toggleStep)
  const setStepVelocity = useSequencerStore(s => s.setStepVelocity)
  const setStepPitch = useSequencerStore(s => s.setStepPitch)
  const setStepFx = useSequencerStore(s => s.setStepFx)
  const setStepMicroOffset = useSequencerStore(s => s.setStepMicroOffset)

  // Ensure the cell is on before applying any lane edit. Matches the
  // "always interactive" promise — touching the visualizer turns the
  // cell on if it wasn't already.
  const ensureOn = () => {
    if (!cell.on) toggleStep(row, stepIndex)
  }

  return (
    <div className="rounded-xl border border-brd-subtle bg-surface-raised/40 overflow-hidden">
      {/* Lane tabs */}
      <div
        className="flex items-center gap-0 border-b border-brd-subtle bg-surface-sunken"
        role="tablist"
        aria-label="Cell editor lane"
      >
        {LANES.map(l => (
          <button
            key={l.id}
            type="button"
            role="tab"
            aria-selected={lane === l.id}
            onClick={() => setLane(l.id)}
            className={`flex-1 px-2 py-1.5 text-[10px] font-mono uppercase tracking-[0.14em] transition-colors ${
              lane === l.id
                ? 'text-accent bg-accent-muted border-b-2 border-accent'
                : 'text-content-tertiary hover:text-content-secondary border-b-2 border-transparent'
            }`}
          >
            {l.label}
          </button>
        ))}
      </div>

      <div className="p-3">
        {lane === 'note' && (
          <NoteLane
            cell={cell}
            onSetMicroOffset={v => {
              ensureOn()
              setStepMicroOffset(row, stepIndex, v)
            }}
            onToggle={() => toggleStep(row, stepIndex)}
          />
        )}
        {lane === 'pitch' && (
          <PitchLane
            cell={cell}
            onSet={v => {
              ensureOn()
              setStepPitch(row, stepIndex, v)
            }}
          />
        )}
        {lane === 'velocity' && (
          <VelocityLane
            cell={cell}
            onSet={v => {
              ensureOn()
              setStepVelocity(row, stepIndex, v)
            }}
          />
        )}
        {lane === 'fx' && (
          <FxLane
            cell={cell}
            onSet={(fxId, v) => {
              ensureOn()
              setStepFx(row, stepIndex, fxId, v)
            }}
          />
        )}
        {lane === 'roll' && (
          <p className="text-[10px] text-content-tertiary italic">
            Roll configuration lives in its own section below — we preserve the
            existing multi-hit micro-grid editor there because its pitch-offsets
            UI doesn&apos;t collapse into a single lane cleanly.
          </p>
        )}
      </div>
    </div>
  )
}

const NoteLane: React.FC<{
  cell: StepCell
  onSetMicroOffset: (v: number | undefined) => void
  onToggle: () => void
}> = ({ cell, onSetMicroOffset, onToggle }) => {
  const microOffset = cell.microOffset ?? 0

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-content-tertiary">
          Off-grid timing
        </span>
        <button
          type="button"
          onClick={onToggle}
          className={`px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider ${
            cell.on
              ? 'bg-accent text-[color:var(--accent-ink)]'
              : 'bg-surface-sunken text-content-tertiary border border-brd-subtle hover:border-[color:var(--accent-halo)]'
          }`}
        >
          {cell.on ? 'ON' : 'OFF'}
        </button>
      </div>
      <div
        className="relative h-16 bg-surface-base border border-brd-subtle rounded-md"
        onClick={e => {
          // Click the timeline to jump the note to that fractional
          // offset. Drag via the slider below for precise values.
          const rect = e.currentTarget.getBoundingClientRect()
          const x = Math.max(
            0,
            Math.min(1, (e.clientX - rect.left) / rect.width),
          )
          const clamped = Math.min(0.999, x)
          onSetMicroOffset(clamped === 0 ? undefined : clamped)
        }}
      >
        {/* Grid tick at 0% (on-grid anchor). */}
        <div
          aria-hidden
          className="absolute top-0 bottom-0 left-0 w-px bg-content-tertiary/50"
        />
        {/* Note block. Width stays fixed; position follows microOffset. */}
        <div
          aria-hidden
          className={`absolute top-1 bottom-1 w-6 rounded ${
            cell.on ? 'bg-accent' : 'bg-content-tertiary/40'
          }`}
          style={{
            left: `calc(${microOffset * 100}% + 2px)`,
            transition: 'left 60ms linear',
          }}
        />
      </div>
      <input
        type="range"
        min={0}
        max={99}
        step={1}
        value={Math.round(microOffset * 100)}
        onChange={e => {
          const v = Number(e.target.value) / 100
          onSetMicroOffset(v === 0 ? undefined : v)
        }}
        className="w-full accent-[color:var(--accent)]"
      />
      <div className="flex items-center justify-between text-[10px] font-mono text-content-tertiary">
        <span>on grid</span>
        <span>{Math.round(microOffset * 100)}% into step</span>
        <button
          type="button"
          onClick={() => onSetMicroOffset(undefined)}
          disabled={microOffset === 0}
          className="hover:text-content-secondary disabled:opacity-40"
        >
          reset
        </button>
      </div>
    </div>
  )
}

const PitchLane: React.FC<{
  cell: StepCell
  onSet: (v: number) => void
}> = ({ cell, onSet }) => {
  const pitch = cell.pitch ?? 0
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-content-tertiary">
          Pitch (semitones)
        </span>
        <span className="text-[11px] font-mono text-content-secondary tabular-nums">
          {pitch === 0 ? '—' : `${pitch > 0 ? '+' : ''}${pitch} st`}
        </span>
      </div>
      <input
        type="range"
        min={-24}
        max={24}
        step={1}
        value={pitch}
        onChange={e => onSet(Number(e.target.value))}
        className="w-full accent-[color:var(--accent)]"
      />
      <div className="flex items-center justify-between text-[10px] font-mono text-content-tertiary">
        <span>-24</span>
        <span>0</span>
        <span>+24</span>
      </div>
      <button
        type="button"
        onClick={() => onSet(0)}
        disabled={pitch === 0}
        className="self-end text-[10px] font-mono text-content-tertiary hover:text-content-secondary disabled:opacity-40"
      >
        reset
      </button>
    </div>
  )
}

const VelocityLane: React.FC<{
  cell: StepCell
  onSet: (v: number | undefined) => void
}> = ({ cell, onSet }) => {
  const velocity = cell.velocity ?? 1
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-content-tertiary">
          Velocity
        </span>
        <span className="text-[11px] font-mono text-content-secondary tabular-nums">
          {cell.velocity === undefined
            ? '— default'
            : `${Math.round(velocity * 100)}%`}
        </span>
      </div>
      <input
        type="range"
        min={0}
        max={100}
        step={1}
        value={Math.round(velocity * 100)}
        onChange={e => onSet(Number(e.target.value) / 100)}
        className="w-full accent-[color:var(--accent)]"
      />
      <button
        type="button"
        onClick={() => onSet(undefined)}
        disabled={cell.velocity === undefined}
        className="self-end text-[10px] font-mono text-content-tertiary hover:text-content-secondary disabled:opacity-40"
      >
        reset
      </button>
    </div>
  )
}

const FxLane: React.FC<{
  cell: StepCell
  onSet: (fxId: FxId, value: number) => void
}> = ({ cell, onSet }) => {
  return (
    <div className="flex flex-col gap-2">
      {FX_IDS.map(id => {
        const supported = PER_STEP_FX.includes(id)
        const value = cell.fx?.[id] ?? 0
        return (
          <div
            key={id}
            className="flex items-center gap-2"
            title={
              supported
                ? undefined
                : 'Filter / tape-stop are master-bus only; use the FX bank below the grid.'
            }
          >
            <span
              className={`w-10 text-[10px] font-mono ${
                supported
                  ? 'text-content-secondary'
                  : 'text-content-tertiary/60'
              }`}
            >
              {FX_LABEL_SHORT[id]}
            </span>
            <input
              type="range"
              min={0}
              max={100}
              step={1}
              value={Math.round(value * 100)}
              onChange={e => onSet(id, Number(e.target.value) / 100)}
              disabled={!supported}
              className="flex-1 accent-[color:var(--accent)]"
            />
            <span className="w-8 text-right text-[10px] font-mono text-content-tertiary tabular-nums">
              {value > 0 ? Math.round(value * 100) : '—'}
            </span>
          </div>
        )
      })}
    </div>
  )
}
